import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LoginForm from './LoginForm';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user, isLoading, isAdmin, isMember, isAdminsVerified, signOut } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm onSuccess={() => {}} />;
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Access Denied</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            You don't have permission to access this area. Admin privileges required.
          </p>
          <div className="mt-6">
            <button
              onClick={() => (window.location.href = '/')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Admin MFA enforcement (Requirement 7): an admin who has not completed a
  // second factor this session (not in AdminsVerified) is redirected to the
  // MFA setup page. The setup page itself is exempt, otherwise the admin could
  // never escape the redirect. Members never reach this point — the
  // `requireAdmin && !isAdmin` block above already shows them Access Denied,
  // including on /admin/setup-mfa (Requirement 6.4).
  if (requireAdmin && isAdmin && !isAdminsVerified) {
    const onSetupPage = window.location.pathname.replace(/\/$/, '') === '/admin/setup-mfa';
    if (!onSetupPage) {
      window.location.href = '/admin/setup-mfa';
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue mx-auto"></div>
            <p className="mt-4 text-gray-600">Redirecting to multi-factor authentication setup…</p>
          </div>
        </div>
      );
    }
    // On the setup page: allow through so the admin can enrol, even though they
    // are not yet in AdminsVerified.
  }

  // Non-admin routes: check for Members or Admins group membership
  if (!requireAdmin && !isMember) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-yellow-100">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Account Not Linked
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 max-w-md">
            Your portal account is not linked to an active club membership. If you're a club member,
            please ensure you registered with the same email address used for your membership.
          </p>
          <p className="mt-2 text-center text-sm text-gray-600 max-w-md">
            If you recently applied for membership, your application may still be under review — you'll receive an email once it's approved.
          </p>
          <div className="mt-6 flex flex-col items-center space-y-3">
            <a
              href="/register"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
            >
              Register for Membership
            </a>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
